import { type ReactNode, type Ref } from 'react';
import classnames from 'classnames';
import { Icon } from 'components/common';
import Button from './Button';
import useHideIntercom from './useHideIntercom';

interface PanelProps {
  children: ReactNode;
  isOpen: boolean;
  ref?: Ref<HTMLDivElement>;
  testId?: string;
}

const Panel = ({ children, isOpen, ref, testId }: PanelProps) => {
  useHideIntercom(isOpen);

  return (
    <div
      className={classnames(
        'panel bg-white border-border border-start-sm d-flex flex-column position-fixed w-100 z-1',
        {
          'panel--open': isOpen,
        }
      )}
      data-testid={testId}
      ref={ref}
    >
      {children}
    </div>
  );
};

interface PanelHeaderProps {
  children?: ReactNode;
  equalXPadding?: boolean;
  onClose?: () => void;
  testId?: string | null;
}

const PanelHeader = ({ children, equalXPadding = false, onClose, testId = null }: PanelHeaderProps) => (
  <div className="border-bottom border-gray px-3" data-testid={testId ? `${testId}-header` : 'panel-header'}>
    <div className="align-items-center d-flex height-15">
      {equalXPadding && onClose && (
        <>
          {/** Width and margin match close button */}
          <div className="ms-n2_5" style={{ width: 38 }} />
        </>
      )}

      {children}

      {onClose && (
        <div className="ms-auto">
          <div className="me-n2_5">
            <Button onClick={onClose} testId={testId ? `${testId}-close-button` : 'panel-close-button'}>
              <Icon name="xmark" />
            </Button>
          </div>
        </div>
      )}
    </div>
  </div>
);

interface PanelBodyProps {
  children: ReactNode;
}

const PanelBody = ({ children }: PanelBodyProps) => <div className="overflow-auto h-100 px-3 pt-4">{children}</div>;

interface PanelFooterButtonProps {
  children: string;
  disabled?: boolean;
  onClick: () => void;
  testId?: string;
}

const PanelFooterButton = ({ children, disabled, onClick, testId = 'panel-footer-button' }: PanelFooterButtonProps) => (
  <div className="p-3 bg-white">
    <Button disabled={disabled} onClick={onClick} testId={testId} text="bold" variant="primary-dark" width="block">
      {children}
    </Button>
  </div>
);

Panel.Header = PanelHeader;
Panel.Body = PanelBody;
Panel.FooterButton = PanelFooterButton;
export default Panel;
